import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Skeleton,
  Tabs,
  Tab,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Star as StarIcon,
  SmartToy as AiIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as TimeIcon,
  SportsSoccer as SportsIcon,
  Lock as LockIcon,
  ExpandMore as ExpandMoreIcon,
  Apps as AllIcon,
  SportsBasketball as NBAIcon,
  SportsHockey as NHLIcon,
  SportsMma as MMAIcon,
  SportsBaseball as MLBIcon,
  SportsFootball as FootballIcon,
  Shield as ConservativeIcon,
  Balance as BalancedIcon,
  Bolt as AggressiveIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { getPicksForHandicapperV2 } from '../../services/handicapperPicksService';
import { SportIcon } from '../../utils/sportIcons';
import { pickMatchesTab, sportKeyToShort, tabToSportKey } from '../../utils/sportsMapping';
import BackButton from '../BackButton';

const TopPicksScreen = () => {
  const navigate = useNavigate();
  const { isPremium, loading: subscriptionLoading } = useSubscription();
  const [bettorAIPicks, setBettorAIPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  const [selectedPersonality, setSelectedPersonality] = useState('all');
  const [expandedCards, setExpandedCards] = useState({});

  // Number of free preview picks for non-premium users
  const FREE_PREVIEW_COUNT = 2;

  // Bookmaker URL mapping
  const getBookmakerUrl = (bookmakerKey) => {
    const bookmakerUrls = {
      'draftkings': 'https://www.draftkings.com',
      'fanduel': 'https://www.fanduel.com',
      'caesars': 'https://www.caesars.com/sportsbook',
      'fanatics': 'https://www.fanaticssportsbook.com',
      'betmgm': 'https://www.betmgm.com',
      'pointsbet': 'https://www.pointsbet.com',
      'barstool': 'https://www.barstoolsportsbook.com',
      'unibet': 'https://www.unibet.com',
      'williamhill': 'https://www.williamhill.com',
      'betrivers': 'https://www.betrivers.com',
      'sugarhouse': 'https://www.sugarhouse.com',
      'twinspires': 'https://www.twinspires.com',
      'wynnbet': 'https://www.wynnbet.com'
    };

    return bookmakerUrls[bookmakerKey?.toLowerCase()] || null;
  };

  // Handle bookmaker button click
  const handleBookmakerClick = (bookmaker) => {
    const url = getBookmakerUrl(bookmaker);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Fetch BettorAI picks on component mount
  useEffect(() => {
    const fetchBettorAIPicks = async () => {
      try {
        setLoading(true);
        
        // Fetch picks from all three AI personalities
        const personalityIds = ['bettor-ai-con', 'bettor-ai-agg', 'bettor-ai-bal'];
        const allPicksPromises = personalityIds.map(id => getPicksForHandicapperV2(id));
        const allPicksData = await Promise.all(allPicksPromises);

        // Combine all picks from all personalities
        const allPicks = allPicksData.flatMap(data => {
          const picksArray = data?.picks || [];
          return picksArray.map(pick => ({
            ...pick,
            // Ensure personality metadata is included
            personalityName: pick.personalityName || data.personalityName,
            personalityColor: pick.personalityColor || data.color,
            personalityIcon: pick.personalityIcon || data.icon
          }));
        });

        // Sort picks by commence time (game time) for better UX
        const sortedPicks = allPicks
          .sort((a, b) => {
            // If both picks have commence time, sort by that (earliest games first)
            if (a.commenceTime && b.commenceTime) {
              return new Date(a.commenceTime) - new Date(b.commenceTime);
            }
            
            // Otherwise, fall back to creation date (newest first)
            const aTime = a.createdAt ? (a.createdAt instanceof Date ? a.createdAt.getTime() : a.createdAt) : 0;
            const bTime = b.createdAt ? (b.createdAt instanceof Date ? b.createdAt.getTime() : b.createdAt) : 0;
            return bTime - aTime;
          });

        setBettorAIPicks(sortedPicks);
      } catch (err) {
        console.error('Error fetching BettorAI picks:', err);
        setError('Failed to load BettorAI picks');
      } finally {
        setLoading(false);
      }
    };

    fetchBettorAIPicks();
  }, []);

  // Format pick display
  const formatPickDisplay = (pick) => {
    const gameTime = pick.commenceTime
      ? new Date(pick.commenceTime).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
      : 'TBD';

    return {
      matchup: pick.pickType === 'outright'
        ? `FIFA World Cup Winner`
        : `${pick.awayTeam} @ ${pick.homeTeam}`,
      gameTime,
      pick: `${pick.pickType}: ${pick.pickedTeam}`,
      odds: pick.odds ? (pick.odds > 0 ? `+${pick.odds}` : `${pick.odds}`) : 'N/A',
      confidence: pick.confidence || 'Medium',
      sport: sportKeyToShort(pick.sport) || 'N/A'
    };
  };

  // Get result color based on pick result
  const getResultColor = (result) => {
    switch (result?.toLowerCase()) {
      case 'win': return 'success.main';
      case 'loss': return 'error.main';
      case 'push': return 'warning.main';
      default: return 'text.secondary';
    }
  };

  // Get confidence color
  const getConfidenceColor = (confidence) => {
    switch (confidence?.toLowerCase()) {
      case 'high': return 'error.main';
      case 'medium': return 'warning.main';
      case 'low': return 'success.main';
      default: return 'primary.main';
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Handle personality filter change
  const handlePersonalityChange = (event, newValue) => {
    setSelectedPersonality(newValue);
  };

  // Get personality sort order (Conservative, Balanced, Aggressive)
  const getPersonalitySortOrder = (personalityName) => {
    const name = personalityName?.toLowerCase() || '';
    if (name.includes('conservative') || name.includes('con')) return 1;
    if (name.includes('balanced') || name.includes('bal')) return 2;
    if (name.includes('aggressive') || name.includes('agg')) return 3;
    return 4; // Unknown personalities go last
  };

  // Filter picks based on selected sport tab and personality, then sort
  const filteredPicks = (() => {
    let picks = bettorAIPicks;
    
    // Apply sport filter
    if (selectedTab !== 'all') {
      picks = picks.filter(pick => pickMatchesTab(pick, selectedTab));
    }
    
    // Apply personality filter
    if (selectedPersonality !== 'all') {
      picks = picks.filter(pick => {
        const personalityName = pick.personalityName?.toLowerCase() || '';
        if (selectedPersonality === 'conservative') return personalityName.includes('conservative') || personalityName.includes('con');
        if (selectedPersonality === 'balanced') return personalityName.includes('balanced') || personalityName.includes('bal');
        if (selectedPersonality === 'aggressive') return personalityName.includes('aggressive') || personalityName.includes('agg');
        return false;
      });
    }
    
    // Sort by personality first, then by commence time
    return picks.sort((a, b) => {
      const personalityCompare = getPersonalitySortOrder(a.personalityName) - getPersonalitySortOrder(b.personalityName);
      if (personalityCompare !== 0) return personalityCompare;
      
      // Then by commence time
      if (a.commenceTime && b.commenceTime) {
        return new Date(a.commenceTime) - new Date(b.commenceTime);
      }
      return 0;
    });
  })();

  // Count picks by sport (considering current personality filter)
  const countPicksBySport = (tabValue) => {
    let picks = bettorAIPicks;
    
    // Apply personality filter first if one is selected
    if (selectedPersonality !== 'all') {
      picks = picks.filter(pick => {
        const personalityName = pick.personalityName?.toLowerCase() || '';
        if (selectedPersonality === 'conservative') return personalityName.includes('conservative') || personalityName.includes('con');
        if (selectedPersonality === 'balanced') return personalityName.includes('balanced') || personalityName.includes('bal');
        if (selectedPersonality === 'aggressive') return personalityName.includes('aggressive') || personalityName.includes('agg');
        return false;
      });
    }
    
    // Then count by sport
    return tabValue === 'all' ? picks.length : picks.filter(pick => pickMatchesTab(pick, tabValue)).length;
  };

  // Count picks by personality (considering current sport filter)
  const countPicksByPersonality = (personalityType) => {
    let picks = bettorAIPicks;
    
    // Apply sport filter first if one is selected
    if (selectedTab !== 'all') {
      picks = picks.filter(pick => pickMatchesTab(pick, selectedTab));
    }
    
    // Then count by personality
    if (personalityType === 'all') return picks.length;
    
    return picks.filter(pick => {
      const personalityName = pick.personalityName?.toLowerCase() || '';
      if (personalityType === 'conservative') return personalityName.includes('conservative') || personalityName.includes('con');
      if (personalityType === 'balanced') return personalityName.includes('balanced') || personalityName.includes('bal');
      if (personalityType === 'aggressive') return personalityName.includes('aggressive') || personalityName.includes('agg');
      return false;
    }).length;
  };

  const getActiveFilterSummary = () => {
    if (selectedTab === 'all' && selectedPersonality === 'all') {
      return 'Try adjusting your sport or personality filters';
    }

    const sportLabel = selectedTab === 'all' ? 'All Sports' : selectedTab.toUpperCase();
    const personalityLabel = selectedPersonality === 'all'
      ? 'All Personalities'
      : `${selectedPersonality.charAt(0).toUpperCase()}${selectedPersonality.slice(1)}`;

    return `Showing: ${sportLabel} + ${personalityLabel}`;
  };

  // Handle card expansion
  const handleExpandClick = (pickId) => {
    setExpandedCards(prev => ({
      ...prev,
      [pickId]: !prev[pickId]
    }));
  };

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        {/* Header */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <AiIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            BettorAI Picks
          </Typography>
          <Typography variant="body1" color="text.secondary">
            AI-powered betting picks and analysis
          </Typography>
        </Box>

        {/* Loading Skeletons */}
        <Grid container spacing={1.5}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" height={32} />
                  <Skeleton variant="text" height={24} sx={{ mt: 1 }} />
                  <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <BackButton />
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <AiIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          BettorAI Picks
        </Typography>
        <Typography variant="body1" color="text.secondary">
          AI-powered betting picks and analysis ({bettorAIPicks.length} total picks)
        </Typography>
      </Box>

      {/* Sport Filter Tabs */}
      {bettorAIPicks.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            Filter by Sport
          </Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  minWidth: { xs: 'auto', sm: 100 }
                }
              }}
            >
              <Tab
                icon={<AllIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`All (${countPicksBySport('all')})`}
                value="all"
              />
              <Tab
                icon={<FootballIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`NFL (${countPicksBySport('nfl')})`}
                value="nfl"
              />
              <Tab
                icon={<NBAIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`NBA (${countPicksBySport('nba')})`}
                value="nba"
              />
              <Tab
                icon={<NBAIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`NCAAB (${countPicksBySport('ncaab')})`}
                value="ncaab"
              />
              <Tab
                icon={<MMAIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`MMA (${countPicksBySport('mma')})`}
                value="mma"
              />
              <Tab
                icon={<MLBIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`MLB (${countPicksBySport('mlb')})`}
                value="mlb"
              />
              <Tab
                icon={<FootballIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`NCAAF (${countPicksBySport('ncaaf')})`}
                value="ncaaf"
              />
              <Tab
                icon={<SportsIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`World Cup (${countPicksBySport('worldcup')})`}
                value="worldcup"
              />
              <Tab
                icon={<SportsIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`World Cup Winner (${countPicksBySport('worldcupwinner')})`}
                value="worldcupwinner"
              />
            </Tabs>
          </Box>
        </Box>
      )}

      {/* AI Personality Filter Tabs */}
      {bettorAIPicks.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            Filter by AI Personality
          </Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedPersonality}
              onChange={handlePersonalityChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  minWidth: { xs: 'auto', sm: 120 }
                }
              }}
            >
              <Tab
                icon={<AiIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`All (${countPicksByPersonality('all')})`}
                value="all"
              />
              <Tab
                icon={<ConservativeIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`Conservative (${countPicksByPersonality('conservative')})`}
                value="conservative"
              />
              <Tab
                icon={<BalancedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`Balanced (${countPicksByPersonality('balanced')})`}
                value="balanced"
              />
              <Tab
                icon={<AggressiveIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`Aggressive (${countPicksByPersonality('aggressive')})`}
                value="aggressive"
              />
            </Tabs>
          </Box>
        </Box>
      )}

      {/* No Picks Message */}
      {bettorAIPicks.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <AiIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No BettorAI picks available yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for AI-generated betting picks
          </Typography>
        </Box>
      )}

      {/* No Picks for Selected Filter Message */}
      {bettorAIPicks.length > 0 && filteredPicks.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <AiIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No picks match your current filters
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getActiveFilterSummary()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try selecting different filters to see more picks
          </Typography>
        </Box>
      )}

      {/* Picks Grid */}
      <Grid container spacing={1.5}>
        {filteredPicks.map((pick, index) => {
          const displayData = formatPickDisplay(pick);
          const isLocked = !isPremium && index >= FREE_PREVIEW_COUNT;
          
          return (
            <Grid item xs={12} sm={6} md={3} key={pick.id}>
              <Card
                sx={{
                  height: '100%',
                  maxHeight: 420,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: isLocked ? 'none' : 'translateY(-2px)',
                    boxShadow: isLocked ? 1 : 4,
                  },
                  border: pick.result !== 'pending' ? 2 : 1,
                  borderColor: pick.result !== 'pending' ? getResultColor(pick.result) : 'divider',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Premium Lock Overlay */}
                {isLocked && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: 'rgba(0, 0, 0, 0.75)',
                      zIndex: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                      textAlign: 'center'
                    }}
                  >
                    <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                      Premium Pick
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>
                      Subscribe to unlock all BettorAI picks and analysis
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<StarIcon />}
                      onClick={() => navigate('/subscription')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Upgrade to Premium
                    </Button>
                  </Box>
                )}
                
                <CardContent sx={{ 
                  flexGrow: 1, 
                  p: 1.5,
                  filter: isLocked ? 'blur(4px)' : 'none',
                  pointerEvents: isLocked ? 'none' : 'auto'
                }}>
                  {/* Sport and Status */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<SportIcon sportKey={pick.sport} fontSize="small" />}
                        label={displayData.sport}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      {pick.personalityName && (
                        <Chip
                          icon={<AiIcon sx={{ fontSize: 14 }} />}
                          label={pick.personalityName}
                          size="small"
                          sx={{
                            bgcolor: pick.personalityColor || 'primary.main',
                            color: 'white',
                            fontWeight: 600,
                            '& .MuiChip-icon': {
                              color: 'white'
                            }
                          }}
                        />
                      )}
                    </Box>
                    {pick.result !== 'pending' && (
                      <Chip
                        label={pick.result.toUpperCase()}
                        size="small"
                        sx={{
                          backgroundColor: getResultColor(pick.result),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                  </Box>

                  {/* Matchup */}
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {displayData.matchup}
                  </Typography>

                  {/* Game Time */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                    <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {displayData.gameTime}
                    </Typography>
                  </Box>

                  {/* Pick Details */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" color="primary" fontWeight="bold">
                      {displayData.pick}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Odds: {displayData.odds} | Confidence:
                      <Chip
                        label={displayData.confidence}
                        size="small"
                        sx={{
                          ml: 0.5,
                          height: 18,
                          fontSize: '0.7rem',
                          backgroundColor: getConfidenceColor(displayData.confidence),
                          color: 'white'
                        }}
                      />
                    </Typography>
                  </Box>

                  {/* Analysis Preview (when collapsed) */}
                  {pick.analysis && !expandedCards[pick.id] && (
                    <Box sx={{ mt: 'auto' }}>
                      <Typography variant="body2" color="text.secondary" sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.4
                      }}>
                        {pick.analysis}
                      </Typography>
                    </Box>
                  )}

                  {/* Expanded Analysis */}
                  <Collapse in={expandedCards[pick.id]} timeout="auto" unmountOnExit>
                    {pick.analysis && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Detailed Analysis
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          {pick.analysis}
                        </Typography>
                      </Box>
                    )}
                    {/* Additional details when expanded */}
                    {pick.reasoning && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Reasoning
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          {pick.reasoning}
                        </Typography>
                      </Box>
                    )}
                  </Collapse>

                  {/* Expand/Collapse Button */}
                  {pick.analysis && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                      <IconButton
                        onClick={() => handleExpandClick(pick.id)}
                        aria-expanded={expandedCards[pick.id]}
                        aria-label="show more"
                        size="small"
                        sx={{
                          transform: expandedCards[pick.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s'
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Box>
                  )}

                  {/* Bookmaker and Action Button */}
                  {pick.bookmaker && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Bookmaker: {pick.bookmaker}
                        </Typography>
                        {getBookmakerUrl(pick.bookmaker) && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleBookmakerClick(pick.bookmaker)}
                            sx={{
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.75rem',
                              textTransform: 'none',
                              borderRadius: 1,
                              backgroundColor: 'warning.main',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'warning.dark'
                              }
                            }}
                          >
                            Visit Site
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default TopPicksScreen;
